import React, { useEffect, useMemo, useState } from "react";
import { Table, Typography, Button } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import './ProjectTable.scss';
import CheckboxModel from '../../CustomModel/CheckboxModel/CheckboxModel';
import VendorModel from '../../CustomModel/VendorModel/VendorModel';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getCashInflowData, getCashOutflowData, getCashInflowDataVersion, getCashOutflowDataVersioned } from '../../../../redux/budgetSlice/budgetSlice';
import {
        generateMonthExecution,
        transformDataCashflow,
} from '../../../../pages/BudgetSubmission/constants';
import Loader from '../../LoadingSpinner/LoadingSpinner';
import { computeCashNetFlow, computeTotalRow } from '../../../../utils/VerticalTotalOfTable';
import { generateColumns, parseDMY } from '../../../../utils/utilFunctions';
import { addMonths, differenceInMonths, format, parse } from 'date-fns';

// import { useSelector } from "react-redux";

const { Text } = Typography;

const parseNum = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : 0;
};

// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
        if (!startDate) return null;

        const [day, month, year] = startDate.split("-").map(Number);

        if (!day || !month || !year) return null;

        return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: BILLED_UPTO (inflow/outflow rows) was rendered straight off the API's
// static field, and the "Upto" title/columns were anchored to this project's
// own UPTO_DATE.
// NEW: Upto = the API's baseline BILLED_UPTO plus every FY's "_formatted"
// value ("YYYY-YYYY_formatted") that falls strictly before the current
// (system-date-derived) FY - same "dynamic upto" rule used across the other
// reports/tables.
const getDynamicUpto = (row = {}, fyStart, baseField) => {
        let sum = parseNum(row[baseField]);

        Object.keys(row).forEach((key) => {
                const match = /^(\d{4})-\d{4}_formatted$/.exec(key);
                if (!match) return;

                const fyStartYear = Number(match[1]);
                if (fyStartYear < fyStart) {
                        sum += parseNum(row[key]);
                }
        });

        return sum.toFixed(2);
};

const ProjectCashFlowTable = ({ addPackageButton = true, version = "C" }) => {
        const { user } = useSelector((state) => state.auth);
        const { cashInflowData, cashOutflowData, cashInflowVersion, cashOutflowVersion, loading } = useSelector((state) => state.budget);
        const { systemDate } = useSelector((state) => state.otherProjects);
        const [searchParams] = useSearchParams();
        const unitType = searchParams?.get('unitType')?.toUpperCase(); // automatically decoded
        const loginType = unitType ?? user?.role;
        const dispatch = useDispatch();
        const location = useLocation();
        const ioNo = location.state?.ioNo || '--';
        const revisedNo = location.state?.rvNo || '--';
        const { projectDates } = useSelector((state) => state.budget);
        const [selectedPackages, setSelectedPackages] = useState([]);
        const [unitCommercial, setUnitCommercial] = useState();
        const [selectedCategory, setSelectedCategory] = useState(null); // Track clicked section
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [dropdownState, setDropdownState] = useState({});

        // OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
        // NEW: derive it from the admin-configured System Start Date, falling back
        // to today's date until systemDate has loaded.
        const CURRENT_FY_START = useMemo(
                () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
                [systemDate]
        );

        // OLD: title was anchored to this project's own UPTO_DATE.
        // const formattedUptoDate = projectDates?.UPTO_DATE
        //         ? format(parse(projectDates.UPTO_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy")
        //         : '';
        const role = sessionStorage.getItem('role');

        const zeroDateParsed = parseDMY(projectDates?.ZERO_DATE);
        const uptoDateParsed = parseDMY(projectDates?.UPTO_DATE);
        const expectedDateParsed = parseDMY(projectDates?.EXPECTED_DATE);
        const unitPlanCashInflowDateParsed = parseDMY(projectDates?.UNIT_CASH_INFLOW_DATE);
        const unitPlanCashOutflowDateParsed = parseDMY(projectDates?.UNIT_CASH_OUTFLOW_DATE);

        // Use the greater date between cash inflow and outflow dates, handling null/undefined
        const unitPlanDateParsed = (unitPlanCashInflowDateParsed && unitPlanCashOutflowDateParsed)
                ? (unitPlanCashInflowDateParsed > unitPlanCashOutflowDateParsed ? unitPlanCashInflowDateParsed : unitPlanCashOutflowDateParsed)
                : unitPlanCashInflowDateParsed || unitPlanCashOutflowDateParsed || null;
        let startMonth = 4;
        let startYear = 2025;

        // OLD: rendered columns started the month after this project's own
        // UPTO_DATE.
        // NEW: anchored to the system-date-derived current FY (April of
        // CURRENT_FY_START) instead - consistent with the dynamic Upto value.
        // if (uptoDateParsed) {
        //         const startMonthDate = addMonths(uptoDateParsed, 1); //  Move to next month
        //         startMonth = startMonthDate.getMonth() + 1; // 1-based month
        //         startYear = startMonthDate.getFullYear();
        // }
        startMonth = 4;
        startYear = CURRENT_FY_START;
        const startMonthNumber =
                zeroDateParsed && uptoDateParsed
                        ? differenceInMonths(uptoDateParsed, zeroDateParsed) + 3
                        : 0;

        const totalMonth =
                zeroDateParsed && expectedDateParsed && unitPlanDateParsed
                        ? differenceInMonths(
                                expectedDateParsed > unitPlanDateParsed ? expectedDateParsed : unitPlanDateParsed,
                                zeroDateParsed
                        ) + 3
                        : zeroDateParsed && expectedDateParsed
                        ? differenceInMonths(expectedDateParsed, zeroDateParsed) + 3
                        : zeroDateParsed && unitPlanDateParsed
                        ? differenceInMonths(unitPlanDateParsed, zeroDateParsed) + 3
                        : 0;
        // const endYear = expectedDateParsed ? expectedDateParsed.getFullYear() : 2026;
        // const endMonth = expectedDateParsed ? expectedDateParsed.getMonth() + 1 : 12;

        let maxDataEndYear = 0;

        const inflowData = version === "C" ? cashInflowData : cashInflowVersion;
        const outflowData = version === "C" ? cashOutflowData : cashOutflowVersion;

        const extractMaxFY = (data) => {
                if (!Array.isArray(data) || data.length === 0) return 0;

                const fyKeys = Object.keys(data[0]).filter(
                        (key) => /^\d{4}-\d{4}$/.test(key)
                );

                if (fyKeys.length === 0) return 0;

                return Math.max(...fyKeys.map((fy) => Number(fy.split("-")[1])));
        };

        maxDataEndYear = Math.max(
                extractMaxFY(inflowData),
                extractMaxFY(outflowData)
        );

        const expectedEndYear = expectedDateParsed
                ? expectedDateParsed.getFullYear()
                : 0;
        
        const unitPlanEndYear = unitPlanDateParsed
                ? unitPlanDateParsed.getFullYear()
                : 0;

        // Get the higher year between EXPECTED_DATE and UNIT_PLAN_DATE
        const higherDateYear = Math.max(expectedEndYear, unitPlanEndYear);

        // Use the higher year as final end year
        const finalEndYear = higherDateYear;
        
        // Dynamic end month based on higher date
        const higherEndDate = expectedDateParsed && unitPlanDateParsed
                ? (expectedDateParsed > unitPlanDateParsed ? expectedDateParsed : unitPlanDateParsed)
                : (expectedDateParsed || unitPlanDateParsed);
        
        const endMonth = higherEndDate ? higherEndDate.getMonth() + 1 : 12;

        const endYear = finalEndYear;

        const [data, setData] = useState([]);

        let columns = [
                {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        children: [
                                {
                                        title: 'Head',
                                        dataIndex: 'DESC',
                                        key: 'DESC',
                                        fixed: 'left',
                                        width: 100,
                                        align: 'center',
                                        render: (text, record) => {
                                                if (record.ADD_PACKAGE_STATUS === 1) {
                                                        return {
                                                                children: (
                                                                        <div className="w-full">
                                                                                <Button
                                                                                        type="icon"
                                                                                        size="small"
                                                                                        icon={
                                                                                                <DownOutlined />
                                                                                        }
                                                                                        onClick={() =>
                                                                                                toggleDropdown(
                                                                                                        record.key,
                                                                                                )
                                                                                        }
                                                                                        className={
                                                                                                'custom_button'
                                                                                        }
                                                                                />
                                                                                <Text>{text}</Text>{' '}
                                                                                &nbsp;
                                                                                {addPackageButton && (
                                                                                        <Button
                                                                                                type="link"
                                                                                                icon={
                                                                                                        <PlusOutlined />
                                                                                                }
                                                                                                onClick={() =>
                                                                                                        handleAddPackage(
                                                                                                                record.key,
                                                                                                        )
                                                                                                }
                                                                                        >
                                                                                                Add
                                                                                                Package
                                                                                        </Button>
                                                                                )}
                                                                                {record.addProduct && (
                                                                                        <Button
                                                                                                type="link"
                                                                                                icon={
                                                                                                        <PlusOutlined />
                                                                                                }
                                                                                                onClick={() =>
                                                                                                        handleAddPackage(
                                                                                                                record.head,
                                                                                                        )
                                                                                                }
                                                                                        >
                                                                                                Add
                                                                                                Product
                                                                                        </Button>
                                                                                )}
                                                                        </div>
                                                                ),
                                                                // props: { colSpan: 4 },
                                                        };
                                                }

                                                if (record.boldHeading) {
                                                        return <Text strong>{text}</Text>;
                                                }

                                                return <div className="w-[200px]">{text}</div>;
                                        },
                                },
                                {
                                        title: 'Unit Commercial',
                                        dataIndex: 'COMMERCIAL_NAME',
                                        key: 'COMMERCIAL_NAME',
                                        fixed: 'left',
                                        width: 150,
                                        align: 'center',
                                },
                                {
                                        title:
                                                loginType === 'UNIT'
                                                        ? 'Product Type'
                                                        : 'Package Type',
                                        dataIndex:
                                                loginType === 'UNIT'
                                                        ? 'PRODUCT_TYPE'
                                                        : 'PACKAGE_TYPE',
                                        key: loginType === 'UNIT' ? 'PRODUCT_TYPE' : 'PACKAGE_TYPE',
                                        fixed: 'left',
                                        width: 150,
                                        align: 'center',
                                },
                                {
                                        title:
                                                loginType === 'UNIT'
                                                        ? 'Product Name'
                                                        : 'Package Name',
                                        dataIndex:
                                                loginType === 'UNIT'
                                                        ? 'PRODUCT_NAME'
                                                        : 'PACKAGE_NAME',
                                        key: loginType === 'UNIT' ? 'PRODUCT_NAME' : 'PACKAGE_NAME',
                                        fixed: 'left',
                                        width: 150,
                                        align: 'center',
                                },
                        ],
                },
                {
                        // OLD: title: `Upto ${formattedUptoDate}`, (anchored to this project's own UPTO_DATE)
                        // NEW: anchored to the system-date-derived current FY.
                        title: `Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'BILLED_UPTO',
                        key: 'BILLED_UPTO',
                        width: 150,
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        const handleAddPackage = (rowKey) => {
                setSelectedCategory(rowKey); // Use rowKey instead of head
                setIsModalOpen(true);
        };

        const toggleDropdown = (rowKey) => {
                setDropdownState((prev) => ({
                        ...prev,
                        [rowKey]: !prev[rowKey], // Use unique row key
                }));
        };

        useEffect(() => {
                const inflowData = version === "C" ? cashInflowData : cashInflowVersion;
                const outflowData = version === "C" ? cashOutflowData : cashOutflowVersion;

                if (inflowData && outflowData) {
                        // OLD: computeTotalRow/transformDataCashflow read BILLED_UPTO
                        // straight off the API's static field.
                        // NEW: replace it with the dynamic value on every row first, so both
                        // the per-row column and the generic Total-row sum stay consistent.
                        const inflowDataWithDynamicUpto = inflowData.map((row) => ({
                                ...row,
                                BILLED_UPTO: getDynamicUpto(row, CURRENT_FY_START, "BILLED_UPTO"),
                        }));
                        const outflowDataWithDynamicUpto = outflowData.map((row) => ({
                                ...row,
                                BILLED_UPTO: getDynamicUpto(row, CURRENT_FY_START, "BILLED_UPTO"),
                        }));

                        const totalRowCashInflow = computeTotalRow(
                                inflowDataWithDynamicUpto,
                                "Cash Inflow - Total"
                        );

                        delete totalRowCashInflow.PACKAGE_NAME;
                        delete totalRowCashInflow.PRODUCT_NAME;
                        delete totalRowCashInflow.PRODUCT_TYPE;
                        delete totalRowCashInflow.COMMERCIAL_NAME;
                        delete totalRowCashInflow.PACKAGE_TYPE;

                        const totalRowCashOutflow = computeTotalRow(
                                outflowDataWithDynamicUpto,
                                "Cash Outflow - Total"
                        );

                        delete totalRowCashOutflow.PACKAGE_NAME;
                        delete totalRowCashOutflow.PRODUCT_NAME;
                        delete totalRowCashOutflow.PRODUCT_TYPE;
                        delete totalRowCashOutflow.COMMERCIAL_NAME;
                        delete totalRowCashOutflow.PACKAGE_TYPE;

                        // ➖ Net Flow
                        const totalRowNetCashflow = computeCashNetFlow(
                                totalRowCashInflow,
                                totalRowCashOutflow
                        );

                        setData([
                                {
                                        key: "1",
                                        DESC: "Month of Execution",
                                        ...generateMonthExecution(
                                                startMonthNumber,
                                                startYear,
                                                startMonth,
                                                totalMonth
                                        ),
                                        boldHeading: true,
                                },
                                totalRowCashInflow,
                                ...transformDataCashflow({
                                        data: inflowDataWithDynamicUpto,
                                        dispatch,
                                }).map((item, index) => ({
                                        key: `inflow-${index + 3}`,
                                        ...item,
                                })),
                                totalRowCashOutflow,
                                ...outflowDataWithDynamicUpto?.map((item, index) => ({
                                        key: `outflow-${index + 8}`,
                                        ...item,
                                })),
                                totalRowNetCashflow,
                        ]);
                }
        }, [
                version,
                cashInflowData,
                cashOutflowData,
                cashInflowVersion,
                cashOutflowVersion,
                CURRENT_FY_START,
        ]);


        useEffect(() => {
                if (selectedPackages.length === 0 || !selectedCategory) return;
                setSelectedCategory(null); // Reset category
        }, [selectedPackages]);

        useEffect(() => {
                if (role !== "ADMIN" && role !== "SUPER_ADMIN") {
                        if (version === "C") {
                                dispatch(getCashInflowData({ IO_NO: ioNo, REV_NO: revisedNo }));
                                dispatch(getCashOutflowData({ IO_NO: ioNo, REV_NO: revisedNo }));
                        } else {
                                dispatch(
                                        getCashInflowDataVersion({
                                                IO_NO: ioNo,
                                                REV_NO: revisedNo,
                                                version,
                                        })
                                );
                                dispatch(
                                        getCashOutflowDataVersioned({
                                                IO_NO: ioNo,
                                                REV_NO: revisedNo,
                                                version,
                                        })
                                );
                        }
                }
        }, [dispatch, role, ioNo, revisedNo, version]);


        return (
                <>
                        {loading ? (
                                <Loader />
                        ) : (
                                <Table
                                        columns={generateColumns(
                                                startYear,
                                                endYear,
                                                startMonth,
                                                endMonth,
                                                columns,
                                                projectDates?.EXPECTED_DATE   
                                        )}
                                        dataSource={data.filter((item) => {
                                                if (!item.isPackage) return true; // Keep normal rows
                                                return dropdownState[item.package_row] || false; // Show/hide package rows dynamically
                                        })}
                                        pagination={false}
                                        bordered
                                        scroll={{ x: 'max-content', y: 400 }}
                                        className="custom-ant-table"
                                        rowClassName={(record, index) => {
                                                if (index === 0) {
                                                        return 'table-row-dark';
                                                } else if (
                                                        record?.DESC === 'Cash Outflow - Total' ||
                                                        record?.DESC === 'Cash Net Flow' ||
                                                        record?.DESC === 'Cash Inflow - Total'
                                                ) {
                                                        return 'table-row-light-blue';
                                                } else {
                                                        return 'table-row-light';
                                                }
                                        }}
                                />
                        )}
                        <div className="p-4">
                                {isModalOpen && (
                                        <>
                                                {loginType === 'UNIT' && (
                                                        <VendorModel
                                                                isOpen={loginType === 'UNIT'}
                                                                onClose={() =>
                                                                        setIsModalOpen(false)
                                                                }
                                                                title="IO Head Supply"
                                                                initialValues={{
                                                                        packageTypes:
                                                                                selectedPackages,
                                                                        unitCommercial:
                                                                                unitCommercial,
                                                                }}
                                                                onSubmit={(values) => {
                                                                        setSelectedPackages(
                                                                                values?.packageTypes,
                                                                        );
                                                                        setUnitCommercial(
                                                                                values?.unitCommercial,
                                                                        );
                                                                }}
                                                                labelHeading="Product"
                                                                placeholder="Search Products"
                                                                supplyOptions={[
                                                                        {
                                                                                value: 'Shop',
                                                                                label: 'Shop',
                                                                        },
                                                                        {
                                                                                value: 'BOI',
                                                                                label: 'BOI',
                                                                        },
                                                                        {
                                                                                value: 'Other',
                                                                                label: 'Other',
                                                                        },
                                                                ]}
                                                                packageOptions={[
                                                                        {
                                                                                code: 'P1',
                                                                                description:
                                                                                        'Basic Package',
                                                                                typeCode: 'BP',
                                                                                type: 'Shop',
                                                                        },
                                                                        {
                                                                                code: 'P2',
                                                                                description:
                                                                                        'Advanced Package',
                                                                                typeCode: 'AP',
                                                                                type: 'Shop',
                                                                        },
                                                                ]}
                                                                options={[
                                                                        {
                                                                                value: 'BAP,Ranipet',
                                                                                label: 'BAP,Ranipet',
                                                                        },
                                                                ]}
                                                        />
                                                )}
                                                {loginType === 'REGION' && (
                                                        <CheckboxModel
                                                                isOpen={loginType !== 'UNIT'}
                                                                onClose={() =>
                                                                        setIsModalOpen(false)
                                                                }
                                                                title="Select Packages"
                                                                initialValues={{ packageTypes: [] }}
                                                                onSubmit={(values) => {
                                                                        setSelectedPackages(
                                                                                values?.packageTypes,
                                                                        );
                                                                        setUnitCommercial(
                                                                                values?.unitCommercial,
                                                                        );
                                                                }}
                                                                labelHeading={'Customer Package'}
                                                                placeholder={'Search'}
                                                                options={[
                                                                        {
                                                                                value: 'PSWR',
                                                                                label: 'PSWR',
                                                                        },
                                                                ]}
                                                        />
                                                )}
                                        </>
                                )}
                        </div>
                </>
        );
};

export default ProjectCashFlowTable;
